import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import uuid from 'uuid';
import queryString from 'query-string';
import { Grid } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { applyRequiredCapitalizations } from '../../../util/TextUtils';
import { DATE_FORMAT, getCurrentTimeInCurrentTimezoneFormatted, getCurrentTimezone } from '../../../api/CallMeNow';
import { bookExperience } from '../../../actions/callMeLater';
import analyticsContext from '../../analytics/Analytics';
import {
  setAppointmentData,
  setExperienceBookingInProgress,
  setExperienceBookingSuccess,
  getNewCustomerSessionId,
} from '../../../actions/experiences';
import { getAtlasId, setAtlasID, createAppointmentInVHT } from '../../../actions/callMeNow';
import Modal from '../../common/Modal/Modal';
import ApptScheduler from '../../common/ApptScheduler/ApptScheduler';
import Spinner from '../../common/Spinner/Spinner';
import { FancyBlueGradientButton, FancyDisabledButton } from '../../common/FancyButton/FancyButton';
import BookingSuccessModal from '../BookingSuccessModal/BookingSuccessModal';
import BookingFailureModal from '../BookingFailureModal/BookingFailureModal';
import BookingModalsParams from './BookingModalsParams';
import SafeConsole from '../../../util/ConsoleUtils';
import { theme } from '../../../../../themes';
import config, { CONFIG_KEY_NAMES } from '../../../config/config';
import { withOnPropChangedAnalytic } from 'react-shisell';

class BookingModal extends Component {
  constructor(props) {
    super(props);
    this.recaptchaRef = React.createRef();
    this.sitekey = config.KEY(CONFIG_KEY_NAMES.recaptcha);
    const app = queryString.parse(this.props.location.search).app;
    this.state = {
      app,
    };

    this.modes = {
      callMeLater: {
        name: 'callMeLater',
        title: 'Book Appointment for Later',
        text:
          'Interested in scheduling your session for a later day and/or time? Select this option and you can customize the appointment time to fit your schedule. ',
        subtitle: 'Enter your information below and customize the appointment time to fit your schedule.',
        submitButtonTitle: applyRequiredCapitalizations('Book Now'),
        submitButtonBusyTitle: applyRequiredCapitalizations('Booking'),
        legalString:
          'By selecting “' +
          applyRequiredCapitalizations('Book Now') +
          ',” you certify that you agree “Yes” to the following: “May we contact you via phone or text?”',
        imageActive: BookingModalsParams['shs'].bookingModal.imageCallMeLaterModalActive,
      },
      callMeNow: {
        name: 'callMeNow',
        title: 'Book Appointment for Now',
        text:
          'Interested in scheduling your session for now? Select this option and you can customize the appointment time to fit your schedule. ',
        subtitle: (
          <span>
            Enter your information below and a Smart Home Expert will call you within {this.props.maxCustomerWaitTime}{' '}
            minutes.
          </span>
        ),
        submitButtonTitle: applyRequiredCapitalizations('Call Me Now'),
        submitButtonBusyTitle: applyRequiredCapitalizations('Connecting'),
        legalString:
          'By selecting “' +
          applyRequiredCapitalizations('Call Me Now') +
          ',” you certify that you agree “Yes” to the following: “May we contact you via phone or text?”',
        imageActive: BookingModalsParams['shs'].bookingModal.imageCallMeLaterModalActive,
      },
    };
  }

  componentDidMount = () => {
    this.props.getNewCustomerSessionId();
    const customerCallbackNumber = this.getNormalizedCustomerCallbackNumber(
      this.props.appointmentData['customerCallbackNumber']
    );
    if (customerCallbackNumber.length === 10 && !this.props.atlasUniversalId) {
      this.props.getAtlasId(this.props.customerSessionId, customerCallbackNumber);
    }
    analyticsContext
      .withAdvancedProps({
        section: 'bookingModal',
        experience: this.props.experienceData && this.props.experienceData.megaUseCase,
        mode: this.props && this.props.experienceBookingModal,
      })
      .screenEvent();
  };

  getNormalizedCustomerCallbackNumber = (customerCallbackNumber) => {
    if (!customerCallbackNumber) {
      return '';
    }
    return customerCallbackNumber.replace(/\D/g, '');
  };

  onApptSchedulerChange = (appointmentData) => {
    if (!appointmentData) {
      return;
    }

    const newCustomerCallbackNumber = this.getNormalizedCustomerCallbackNumber(
      appointmentData['customerCallbackNumber']
    );
    const currentCustomerCallbackNumber = this.getNormalizedCustomerCallbackNumber(
      this.props.appointmentData.customerCallbackNumber
    );
    if (newCustomerCallbackNumber.length === 10 && newCustomerCallbackNumber !== currentCustomerCallbackNumber) {
      this.props.getAtlasId(this.props.customerSessionId, newCustomerCallbackNumber);
    } else if (newCustomerCallbackNumber.length !== 10 && this.props.atlasUniversalId) {
      this.props.setAtlasID('');
    }

    this.props.setAppointmentData({
      ...appointmentData,
    });
  };

  postCallMeLater = (appointmentData) => {
    this.props.setExperienceBookingInProgress(true);
    const customerCallbackNumber = this.getNormalizedCustomerCallbackNumber(appointmentData.customerCallbackNumber);
    const bookingPayload = {
      ...appointmentData,
      ...this.props.experienceData,
      extraQuestions: this.props.onboardingAnswers,
      customerCallbackNumber,
      appointmentType: 'callmelater',
    };
    this.props
      .bookExperience(bookingPayload)
      .then(() => {
        this.onBookingSuccess(this.modes.callMeLater.name);
      })
      .catch(() => {
        this.onBookingFailure(this.modes.callMeLater.name);
      });
  };

  postCallMeNow = (attempt = 1, appointmentData) => {
    if (attempt < 1) {
      return; // Should happen possibly only in dev ....
    }

    this.props.setExperienceBookingInProgress(true);

    const payload = {
      formattedCallbackNumber: this.getNormalizedCustomerCallbackNumber(appointmentData.customerCallbackNumber),
      callId: appointmentData.customerSessionId,
      captchaResponse: appointmentData.captchaResponse,
      universalId: this.props.atlasUniversalId,
      messageId: appointmentData.messageId,
      agentAssignStatus: true, // For V1 call.
      assignedAgentName: 'VHT', // For V1 call.
      endpointId: '',
      applicationId: '',
      megaUseCase: appointmentData.megaUseCase,
      vhtSegment: appointmentData.vhtSegment,
      channel: 'ATTOnboarding', // TODO: is this correct ??? this.props.channel,
      csdbChannel: 'ATTOnboarding', // TODO: ATTOnboarding ???
      attDeviceSetup: false,
      customerFirstName: appointmentData.customerFirstName,
      customerLastName: appointmentData.customerLastName,
      customerOldDeviceModel: '',
      customerNewDeviceModel: '',
      customerCity: '',
      customerAppointmentDescription: '',
      storeLocation: 'DestinationSite', // ???
      storeName: 'DestinationSite', // ??? For V1 call.
      CTI_CLIENT: 'BMI',
      language: 'en-US',
      vhtClientcode: 'ATT_MPC',
      sourceDomain: window.location.hostname, // For V1 call.
      appointmentType: 'callmenow',
      extraQuestions: this.props.onboardingAnswers,
    };

    // Make upto 5 attempts, upto maxCustomerWaitTime, in 5 minutes increment. Minimum request time is 2 minutes from now.
    if (attempt <= 5) {
      var requestTimeInCurrentTimezone = getCurrentTimeInCurrentTimezoneFormatted(DATE_FORMAT);
      var nextSlotTrialInMinutes = Math.min(Math.max((attempt - 1) * 5, 2), this.props.maxCustomerWaitTime);
      requestTimeInCurrentTimezone = moment
        .tz(requestTimeInCurrentTimezone, getCurrentTimezone())
        .add(nextSlotTrialInMinutes, 'minutes')
        .format(DATE_FORMAT);
      this.props.createAppointmentInVHT(
        attempt,
        payload,
        requestTimeInCurrentTimezone,
        this.createAppointmentInVHTCallback.bind(this)
      );
    } else {
      this.props.setExperienceBookingInProgress(false);
      this.onBookingFailure(this.modes.callMeNow.name);
    }
  };

  createAppointmentInVHTCallback = (vhtResponse) => {
    SafeConsole.log('createAppointmentInVHTCallback : vhtResponse = ', vhtResponse);
    if (vhtResponse) {
      if (vhtResponse.sendToV1Success) {
        this.props.setExperienceBookingInProgress(false);
        this.onBookingSuccess(this.modes.callMeNow.name);
      } else {
        this.postCallMeNow(vhtResponse.attempt + 1, this.props.appointmentData);
      }
    }
  };

  isCallMeNowAvailable = () => {
    return (
      this.props.callMeNowResponse &&
      this.props.callMeNowResponse.waitInMinutes <= this.props.maxCustomerWaitTime &&
      !!this.props.callMeNowResponse.vhtSlots.length
    );
  };

  bookExperience = (e) => {
    e.preventDefault();
    this.recaptchaRef.current.execute();
  };

  onCaptchaVerify = () => {
    const megaUseCase = this.props.experienceData.megaUseCase;
    const customerSessionId = this.props.experienceData.customerSessionId;
    const atlasUniversalId = this.props.atlasUniversalId;
    const captchaResponse = this.recaptchaRef.current.getValue();
    const appointmentData = {
      ...this.props.appointmentData,
      megaUseCase,
      customerSessionId,
      atlasUniversalId,
      captchaResponse,
    };

    this.props.setAppointmentData(appointmentData);

    const currentMode = this.props.experienceBookingModal;
    if (currentMode === 'callMeLater') {
      this.postCallMeLater(appointmentData);
    } else if (currentMode === 'callMeNow') {
      this.postCallMeNow(1, appointmentData);
    }
    const {
      appointmentData: { customerFirstName },
    } = this.props;

    if (customerFirstName && !customerFirstName.toLowerCase().includes('asurion')) {
      analyticsContext
        .withAdvancedProps({
          type: 'button',
          action: 'bookExperience',
          mode: currentMode,
          text: this.submitButtonTitle,
        })
        .controlEvent();
    }
  };

  onClose = () => {
    analyticsContext.withAdvancedProps({ action: 'closeBookingModal', type: 'button' }).controlEvent();
    this.props.parentCloseHandler();
  };

  fireBookingEvent = (bookingType, bookingResult) => {
    analyticsContext
      .withAdvancedProps({
        action: 'booking',
        bookingResult,
        bookingType,
        experience: this.props.experienceData && this.props.experienceData.megaUseCase,
      })
      .sessionEvent();
  };

  onBookingSuccess = (bookingType) => {
    this.fireBookingEvent(bookingType, 'success');
    this.props.setExperienceBookingSuccess(true);
  };

  onBookingFailure = (bookingType) => {
    this.fireBookingEvent(bookingType, 'failure');
    this.props.setExperienceBookingSuccess(false);
  };

  bookingResultsModalCloseHandler = () => {
    this.props.setExperienceBookingSuccess(null);
    this.props.setExperienceBookingInProgress(false);
    if (this.props.experienceBookingSuccess) {
      this.props.parentCloseHandler();
    }
  };

  renderCloseButton = () => {
    const isCloseButtonDisabled = this.props.experienceBookingInProgress;
    return (
      <div>
        <span
          className="closeButton"
          style={{ color: theme.colors.primaryAccentOnWhite }}
          onClick={isCloseButtonDisabled ? null : this.onClose}
        >
          &times;
        </span>
      </div>
    );
  };

  renderTitle = (currentMode) => {
    return (
      <div className="titleContainer">
        <div className="bookingModalTitle">
          <div className="bookingModalText secondaryHeaderFonts">
            {(this.props.experienceToBook.bookingModal && this.props.experienceToBook.bookingModal.title) ||
              this.props.experienceToBook.title}
          </div>
        </div>
        <div>
          <div className="subtitle subtitleFonts">{this.modes[currentMode].subtitle}</div>
        </div>
      </div>
    );
  };

  renderBody = () => {
    const currentMode = this.props.experienceBookingModal;
    return (
      <div className="bodyContainer">
        {this.renderTitle(currentMode)}
        {this.RenderInputs(currentMode)}
      </div>
    );
  };

  RenderInputs = (currentMode) => {
    const bookingModalParams = BookingModalsParams['shs'].bookingModal;
    const buttonBackgroundImage = bookingModalParams.buttonBackgroundImage;

    let isSubmitDisabled =
      this.props.experienceBookingInProgress ||
      !this.props.appointmentData.customerFirstName ||
      //   !this.props.appointmentData.customerLastName ||
      !this.props.appointmentData.customerCallbackNumber ||
      !this.props.atlasUniversalId;

    if (!isSubmitDisabled) {
      if (currentMode === 'callMeLater') {
        isSubmitDisabled =
          !this.props.appointmentData.customerEmailAddress ||
          !this.props.appointmentData.timezone ||
          !this.props.appointmentData.customerAppointmentDateTime;
      } else if (currentMode === 'callMeNow') {
        isSubmitDisabled = !this.isCallMeNowAvailable();
      }
    }

    this.submitButtonTitle = this.modes[currentMode].submitButtonTitle;
    const submitButtonBusyTitle = this.modes[currentMode].submitButtonBusyTitle;
    const submitButtonColor = theme.colors.primaryAccent;
    const submitButtonTextColor = theme.colors.experiencesCallMeModalButtonTextColor;

    return (
      <div id="FormInput">
        <ApptScheduler onApptSchedulerChange={this.onApptSchedulerChange} />
        <div className="footer">
          <div className="line1">
            <ReCAPTCHA
              ref={this.recaptchaRef}
              size="invisible"
              sitekey={this.sitekey}
              onChange={this.onCaptchaVerify}
            />
            {isSubmitDisabled ? (
              <div className="disabledButtonContainer">
                {this.props.experienceBookingInProgress && this.props.experienceBookingSuccess === null ? (
                  <div className="spinnerContainer">
                    <Spinner />
                  </div>
                ) : null}
                <a className="btn inGreenBook" disabled onClick={null}>
                  {this.props.experienceBookingInProgress && this.props.experienceBookingSuccess === null
                    ? submitButtonBusyTitle
                    : this.submitButtonTitle}
                </a>
              </div>
            ) : (
              <a
                className="btn inGreenBook"
                style={{
                  backgroundColor: submitButtonColor,
                  borderColor: submitButtonColor,
                  color: submitButtonTextColor,
                }}
                onClick={this.bookExperience}
              >
                {this.submitButtonTitle}
              </a>
            )}
          </div>
          <div className="line2">{this.modes[currentMode].legalString}</div>
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="bookExperienceModalsContainer">
        {this.props.experienceBookingSuccess === null && (
          <Modal>
            <Grid>
              <div className="bookExperienceModalContent">
                {this.renderCloseButton()}
                {this.renderBody()}
                {this.props.experienceBookingInProgress && <div className="bookingModalBusyOverlay" />}
              </div>
            </Grid>
          </Modal>
        )}
        {this.props.experienceBookingSuccess === true && (
          <BookingSuccessModal onClose={this.bookingResultsModalCloseHandler} />
        )}
        {this.props.experienceBookingSuccess === false && (
          <BookingFailureModal onClose={this.bookingResultsModalCloseHandler} />
        )}
      </div>
    );
  }
}

BookingModal.propTypes = {
  parentCloseHandler: PropTypes.func.isRequired,
  experienceToBook: PropTypes.object.isRequired,
};

const mapStateToProps = (state /*, ownProps */) => ({
  experienceToBook: state.experiences.experienceSelected,
  experienceBookingModal: state.experiences.experienceBookingModal,
  experienceBookingInProgress: state.experiences.experienceBookingInProgress,
  experienceBookingSuccess: state.experiences.experienceBookingSuccess,
  atlasUniversalId: state.callMeNow.atlasUniversalId,
  callMeNowResponse: state.callMeNow.callMeNowResponse,
  maxCustomerWaitTime: state.callMeNow.maxCustomerWaitTime,

  appointmentData: {
    appointmentType: state.experiences.appointmentData.appointmentType,
    customerFirstName: state.experiences.appointmentData.customerFirstName,
    messageId: state.experiences.appointmentData.messageId,
    customerCallbackNumber: state.experiences.appointmentData.customerCallbackNumber,
    customerEmailAddress: state.experiences.appointmentData.customerEmailAddress,
    timezone: state.experiences.appointmentData.timezone,
    customerAppointmentDateTime: state.experiences.appointmentData.customerAppointmentDateTime,
    customerAppointmentDateTimeString: state.experiences.appointmentData.customerAppointmentDateTimeString,
    vhtSegment: state.experiences.experienceSelected.vhtSegment,
  },

  experienceData: {
    customerSessionId: state.experiences.customerSessionId,
    distinctId: state.experiences.distinctId,
    repBrowserInfo: state.experiences.repBrowserInfo,
    repDeviceInfo: state.experiences.repDeviceInfo,
    sourceDomain: state.experiences.sourceDomain,
    vhtSegment: state.experiences.experienceSelected.vhtSegment,
    megaUseCase: state.experiences.experienceSelected.megaUseCase,
  },
  onboardingAnswers: state.experiences.onboardingAnswers,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setAppointmentData,
      bookExperience,
      setExperienceBookingSuccess,
      setExperienceBookingInProgress,
      getAtlasId,
      setAtlasID,
      createAppointmentInVHT,
      getNewCustomerSessionId,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withOnPropChangedAnalytic(
    'experienceBookingSuccess',
    'SHS-EXPERIENCES_SessionEvent',
    (prev, next) => prev === null && (next === true || next === false),
    (props) => {
      return { ...props, Tags: ['atlas'], SendAsCommonEvent: true };
    }
  )(withRouter(BookingModal))
);



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/experiences/components/experiences/BookingModal/BookingModal.js